import moment from 'moment-timezone';
import { ViewersStatsVideoMeta } from './transform-viewers-stats-for-uplot';

/**
 * Returns true when `targetUnix` falls inside the video window:
 * after start and before end (or still live when `endedAt` is missing).
 */
function videoContainsTargetTime(
  video: ViewersStatsVideoMeta,
  targetUnix: number,
  timezone: string,
): boolean {
  if (!video.startedAt) {
    return false;
  }

  const startUnix = moment(video.startedAt).tz(timezone).unix();
  if (targetUnix < startUnix) {
    return false;
  }

  if (!video.endedAt) {
    return true;
  }

  const endUnix = moment(video.endedAt).tz(timezone).unix();
  return targetUnix <= endUnix;
}

/**
 * Picks the video for a channel that was airing at `targetUnix`.
 * Prefers still-live videos over ended ones; among ties, picks the most recently started match.
 */
export function findVideoForChannel(
  videos: Record<string, ViewersStatsVideoMeta> | undefined,
  channelId: string,
  targetUnix: number,
  timezone: string,
): ViewersStatsVideoMeta | undefined {
  if (!videos) {
    return undefined;
  }

  const matching = Object.values(videos).filter(
    (video) =>
      video.channelId === channelId && videoContainsTargetTime(video, targetUnix, timezone),
  );

  if (matching.length === 0) {
    return undefined;
  }

  return matching.sort((a, b) => {
    const aIsLive = a.endedAt ? 0 : 1;
    const bIsLive = b.endedAt ? 0 : 1;

    if (aIsLive !== bIsLive) {
      return bIsLive - aIsLive;
    }

    return moment(b.startedAt).valueOf() - moment(a.startedAt).valueOf();
  })[0];
}

/** YouTube Live DVR only allows seeking up to 12 hours back from the live edge. */
const YOUTUBE_LIVE_MAX_REWIND_SECONDS = 12 * 60 * 60;

const YOUTUBE_LINK_ALL_QUERY_PARAM = 'youtube_link_all';

/** True when `?youtube_link_all` is present in the page URL (bypasses live 12h DVR gating). */
function isYoutubeLinkAllQueryParamEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return new URLSearchParams(window.location.search).has(YOUTUBE_LINK_ALL_QUERY_PARAM);
}

/**
 * Whether the chart hover may offer opening YouTube at the hovered moment.
 * Ended streams are always eligible; live streams are not once they exceed the DVR window.
 */
export function canShowYoutubeWatchActionForVideo(
  video: Pick<ViewersStatsVideoMeta, 'startedAt' | 'endedAt'>,
  timezone: string,
  nowUnix: number = Math.floor(Date.now() / 1000),
): boolean {
  if (isYoutubeLinkAllQueryParamEnabled()) {
    return true;
  }

  if (video.endedAt) {
    return true;
  }

  const startUnix = moment(video.startedAt).tz(timezone).unix();
  return nowUnix - startUnix <= YOUTUBE_LIVE_MAX_REWIND_SECONDS;
}

/**
 * Seconds into the video at the hovered chart timestamp.
 * Ended streams use offset from `startedAt`. Live streams respect YouTube's 12h DVR window.
 */
export function computeVideoOffsetSeconds(
  hoveredUnix: number,
  video: Pick<ViewersStatsVideoMeta, 'startedAt' | 'endedAt'>,
  timezone: string,
  nowUnix: number = Math.floor(Date.now() / 1000),
): number {
  const startUnix = moment(video.startedAt).tz(timezone).unix();

  if (video.endedAt) {
    const endUnix = moment(video.endedAt).tz(timezone).unix();
    const durationSeconds = Math.max(0, endUnix - startUnix);
    const offsetSeconds = Math.floor(hoveredUnix - startUnix);

    return Math.min(durationSeconds, Math.max(0, offsetSeconds));
  }

  if (isYoutubeLinkAllQueryParamEnabled()) {
    return Math.max(0, Math.floor(hoveredUnix - startUnix));
  }

  const earliestSeekableUnix = nowUnix - YOUTUBE_LIVE_MAX_REWIND_SECONDS;
  const effectiveStartUnix = Math.max(startUnix, earliestSeekableUnix);
  const offsetSeconds = Math.floor(hoveredUnix - effectiveStartUnix);

  return Math.min(YOUTUBE_LIVE_MAX_REWIND_SECONDS, Math.max(0, offsetSeconds));
}

/**
 * Builds a YouTube watch URL with the `t` seek parameter in seconds.
 */
export function buildYoutubeWatchUrlAtOffset(videoId: string, offsetSeconds: number): string {
  const url = new URL('https://www.youtube.com/watch');
  url.searchParams.set('v', videoId);
  url.searchParams.set('t', String(Math.max(0, Math.floor(offsetSeconds))) + 's');
  return url.toString();
}
